import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DatePickerPanel from "./DatePickerPanel";
import useExitTransition from "../../hooks/useExitTransition";

// =============================================================================
// Posiciona o DatePickerPanel:
//  • telas largas e altas: popover ancorado no campo (abre embaixo; vira para
//    cima se faltar espaço; sempre inteiro dentro da janela) — acompanha
//    rolagem/redimensionar;
//  • telas estreitas (< 640 px) ou baixas (< 500 px, ex.: celular deitado):
//    diálogo centralizado com fundo escurecido.
// Fecha com Esc, clique fora ou Cancelar. A saída é animada.
// =============================================================================

const POPOVER_WIDTH = 353;
const GAP = 6;
const MOBILE_BREAKPOINT = 640;
const MIN_POPOVER_HEIGHT = 500;
const EDGE = 8;

// O clique/toque que fecha o calendário "por fora" não deve agir também no que
// está embaixo (fechar a modal, apertar um botão…): o 1º clique fora fecha SÓ o
// calendário e o 2º é que vale para o resto. O calendário marca o instante em que
// fechou por fora; quem decide o que fazer com o clique pergunta, por
// `consumeCalendarOutsideClose()` (devolve `true` uma única vez, até ~0,8 s depois
// — o `click` chega logo após o `mousedown`/`touchstart` que fechou).
let outsideCloseAt = 0;
export const consumeCalendarOutsideClose = () => {
  const hit = outsideCloseAt > 0 && Date.now() - outsideCloseAt < 800;
  outsideCloseAt = 0;
  return hit;
};

export default function DatePickerPopover({
  open,
  anchor, // HTMLElement
  onClose,
  extra = null, // conteúdo opcional abaixo do calendário (ex.: hora)
  ...panelProps
}) {
  const { render, closing } = useExitTransition(open, 160);
  const [pos, setPos] = useState(null);
  const [mobile, setMobile] = useState(false);
  const wrapRef = useRef(null);

  const measure = useCallback(() => {
    const isMobile =
      window.innerWidth < MOBILE_BREAKPOINT || window.innerHeight < MIN_POPOVER_HEIGHT;
    setMobile(isMobile);
    if (isMobile || !anchor || !anchor.isConnected) {
      setPos(null);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const height = wrapRef.current ? wrapRef.current.offsetHeight : 440;
    const below = window.innerHeight - rect.bottom - GAP - EDGE;
    const above = rect.top - GAP - EDGE;
    const up = below < height && above > below;
    const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - EDGE));
    // Sempre inteiro na janela: se não couber nem em cima nem embaixo, desliza
    // para dentro da tela (cobrindo parte do campo) em vez de sair pela borda.
    const wanted = up ? rect.top - GAP - height : rect.bottom + GAP;
    const top = Math.max(EDGE, Math.min(wanted, window.innerHeight - height - EDGE));
    setPos({ left, top, up });
  }, [anchor]);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const follow = (event) => {
      if (wrapRef.current && wrapRef.current.contains(event.target)) return;
      measure();
    };
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, measure]);

  // Esc fecha de qualquer foco (o painel pode ainda não ter recebido o foco: ele
  // nasce escondido até medir a posição).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  // Foco no painel assim que ele fica visível (posição medida).
  useEffect(() => {
    if (!open || (!pos && !mobile) || !wrapRef.current) return;
    const panel = wrapRef.current.querySelector(".dp-panel");
    if (panel && !panel.contains(document.activeElement)) {
      panel.focus({ preventScroll: true });
    }
  }, [open, pos, mobile]);

  // Clique/toque fora fecha (no mouse e no toque).
  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (event) => {
      if (wrapRef.current && wrapRef.current.contains(event.target)) return;
      if (anchor && anchor.contains(event.target)) return; // o campo trata
      outsideCloseAt = Date.now();
      onClose?.();
    };
    document.addEventListener("mousedown", onOutside, true);
    document.addEventListener("touchstart", onOutside, { capture: true, passive: true });
    return () => {
      document.removeEventListener("mousedown", onOutside, true);
      document.removeEventListener("touchstart", onOutside, true);
    };
  }, [open, anchor, onClose]);

  if (!render) return null;

  const style = mobile
    ? undefined
    : pos
      ? { position: "fixed", zIndex: 10060, width: POPOVER_WIDTH, left: pos.left, top: pos.top }
      : { position: "fixed", zIndex: 10060, width: POPOVER_WIDTH, left: -9999, top: 0, visibility: "hidden" };

  const body = (
    <div
      ref={wrapRef}
      className={`dp-pop${mobile ? " dp-pop--modal" : ""}${pos?.up ? " dp-pop--up" : ""}${closing ? " dp-pop--closing" : ""}`}
      style={style}
    >
      <DatePickerPanel {...panelProps} extra={extra} onCancel={onClose} />
    </div>
  );

  return createPortal(
    mobile ? (
      <div className={`dp-backdrop${closing ? " dp-backdrop--closing" : ""}`}>{body}</div>
    ) : (
      body
    ),
    document.body,
  );
}
